import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { useAuth } from '@/lib/auth';
import { useFeature } from '@/lib/features';
import {
  Target, Plus, CheckCircle2, AlertTriangle, Users, Calendar,
  Layers, Flag, TrendingUp, BarChart3, ClipboardCheck, ShieldCheck, Trash2, Pencil,
  Flame, TimerReset, ArrowRight, History, Send,
} from 'lucide-react';
import { STAGE_LABEL, STAGES, normalizeStage } from '@/lib/roles';
import { ShiftTargetDialog } from '@/pages/MyWeekPage';
import { WeeklyRecapCard } from '@/components/rhythm/WeeklyRecapCard';
import { todayISO } from '@/lib/dates';

const LEAD_ROLES = ['owner', 'operations', 'dm_creative', 'dm_technical', 'dm_architecture', 'hr'];
const SHIFT_ROLES = ['owner', 'operations', 'dm_creative', 'dm_technical', 'dm_architecture'];
const STATUS_CHIP = {
  open: 'chip-info',
  overdue: 'chip-danger',
  done: 'chip-success',
  cancelled: '',
};

export default function CommitmentsPage() {
  const { user } = useAuth();
  const isLead = LEAD_ROLES.includes(user.role);
  const canShift = SHIFT_ROLES.includes(user.role);
  const enabled = useFeature('operating_rhythm_v4');
  const [tab, setTab] = useState('me');
  const [meData, setMeData] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [brief, setBrief] = useState(null);
  const [meta, setMeta] = useState(null);
  const [projects, setProjects] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(null);
  const [openRootCause, setOpenRootCause] = useState(null);
  const [openShift, setOpenShift] = useState(null);
  const [openHistory, setOpenHistory] = useState(null);
  const [openComplete, setOpenComplete] = useState(null);
  const [openDelete, setOpenDelete] = useState(null);
  const [pendingShifts, setPendingShifts] = useState([]);
  const [digest, setDigest] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const loadAll = async () => {
    try {
      const [m, mt, ps] = await Promise.all([
        api.get('/commitments/me?window=all'),
        api.get('/rhythm/meta'),
        api.get('/projects'),
      ]);
      setMeData(m.data); setMeta(mt.data); setProjects(ps.data || []);
      if (isLead) {
        const [t, b] = await Promise.all([
          api.get('/commitments/team'),
          api.get('/alignment/wednesday'),
        ]);
        setTeamData(t.data); setBrief(b.data);
      } else {
        const b = await api.get('/alignment/wednesday/me');
        setBrief(b.data);
      }
      if (canShift) {
        try {
          const q = await api.get('/commitments/shifts/pending');
          setPendingShifts(q.data?.rows || []);
        } catch { setPendingShifts([]); }
      }
    } catch (e) { toast.error(parseErr(e)); }
  };

  useEffect(() => { loadAll(); }, []);

  // Deep-link support: the Monday shift-digest notification links to
  // /commitments?tab=team&digest=1 — auto-select the digest tab.
  // We only honour URL params for tabs the current user has access to.
  useEffect(() => {
    const wantTab = searchParams.get('tab');
    const wantDigest = searchParams.get('digest');
    if (wantDigest === '1' && canShift) {
      setTab('digest');
    } else if (wantTab === 'me' || wantTab === 'brief') {
      setTab(wantTab);
    } else if (wantTab === 'team' && isLead) {
      setTab('team');
    } else if (wantTab === 'digest' && canShift) {
      setTab('digest');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lazy-load the digest only when the tab is opened.
  useEffect(() => {
    if (tab !== 'digest' || !canShift) return;
    if (digest) return;
    setDigestLoading(true);
    api.get('/commitments/shift_digest?days=7')
      .then(r => setDigest(r.data))
      .catch(e => toast.error(parseErr(e)))
      .finally(() => setDigestLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, canShift]);

  return (
    <AppShell breadcrumbs={[{ label: 'Commitments & Alignment' }]}>
      <div className="p-6 max-w-[1400px] mx-auto space-y-4 fade-up" data-testid="commitments-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
              <Target size={22} />Commitments &amp; Alignment
            </h1>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))] mt-1 max-w-[720px]">
              You set the target dates. Moving one later is allowed and logged &mdash; two self-moves quietly flag overload to Ops.
              A lead or Ops can <b>Shift target</b> with a reason: that’s the re-baseline path, and it forgives KRA penalty from that point.
              Overdue items still ask for a 10-second root cause so patterns stay honest.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setOpenAdd(true)} data-testid="commitments-add-cta">
              <Plus size={14} className="mr-1" />New commitment
            </Button>
          </div>
        </div>

        {enabled === false && (
          <div className="card p-3 text-[13px] text-[hsl(var(--foreground-muted))]">
            Operating Rhythm v4 is not enabled for your role yet.
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="me" data-testid="commitments-tab-me">
              <Target size={13} className="mr-1" />My commitments {meData?.counts ? `(${meData.counts.open + meData.counts.overdue})` : ''}
            </TabsTrigger>
            <TabsTrigger value="brief" data-testid="commitments-tab-brief">
              <BarChart3 size={13} className="mr-1" />Wednesday Alignment
            </TabsTrigger>
            {isLead && (
              <TabsTrigger value="team" data-testid="commitments-tab-team">
                <Users size={13} className="mr-1" />Team
              </TabsTrigger>
            )}
            {canShift && (
              <TabsTrigger value="digest" data-testid="commitments-tab-digest">
                <Send size={13} className="mr-1" />
                Shift digest
                {digest?.summary && (digest.summary.shift_events + digest.summary.overloads) > 0 && (
                  <span className="ml-1.5 chip chip-warning tabular-nums">
                    {digest.summary.shift_events + digest.summary.overloads}
                  </span>
                )}
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>

        {tab === 'me' && meData && (
          <MyCommitments
            data={meData}
            projects={projects}
            onOpenEdit={setOpenEdit}
            onOpenRootCause={setOpenRootCause}
            onOpenHistory={setOpenHistory}
            onComplete={setOpenComplete}
            onDelete={setOpenDelete}
          />
        )}

        {tab === 'brief' && brief && (
          <WednesdayBrief brief={brief} isLead={isLead} />
        )}

        {tab === 'team' && isLead && teamData && (
          <TeamCommitments
            data={teamData}
            canShift={canShift}
            pendingShifts={pendingShifts}
            onOpenShift={setOpenShift}
            onOpenHistory={setOpenHistory}
            onConfirmShift={async (commitmentId, shiftId) => {
              try {
                await api.post(`/commitments/${commitmentId}/confirm_shift`, { shift_id: shiftId });
                toast.success('Re-baseline confirmed.');
                loadAll();
              } catch (e) { toast.error(parseErr(e)); }
            }}
          />
        )}

        {tab === 'digest' && canShift && (
          <div className="space-y-3">
            <WeeklyRecapCard />
            <ShiftDigestPanel
              digest={digest}
              loading={digestLoading}
              onOpenHistory={setOpenHistory}
              onReload={async () => {
                setDigestLoading(true);
                try {
                  const r = await api.get('/commitments/shift_digest?days=7');
                  setDigest(r.data);
                } catch (e) { toast.error(parseErr(e)); }
                finally { setDigestLoading(false); }
              }}
            />
          </div>
        )}
      </div>

      {openAdd && meta && (
        <CommitmentDialog
          mode="create"
          meta={meta}
          projects={projects}
          onClose={() => setOpenAdd(false)}
          onDone={() => { setOpenAdd(false); loadAll(); }}
        />
      )}
      {openEdit && meta && (
        <CommitmentDialog
          mode="edit"
          meta={meta}
          projects={projects}
          initial={openEdit}
          onClose={() => setOpenEdit(null)}
          onDone={() => { setOpenEdit(null); loadAll(); }}
        />
      )}
      {openRootCause && meta && (
        <RootCauseDialog
          commitment={openRootCause}
          meta={meta}
          onClose={() => setOpenRootCause(null)}
          onDone={() => { setOpenRootCause(null); loadAll(); }}
        />
      )}
      {openShift && (
        <ShiftTargetDialog
          commitment={openShift}
          projects={projects}
          onClose={() => setOpenShift(null)}
          onDone={() => { setOpenShift(null); loadAll(); }}
        />
      )}
      {openHistory && (
        <PriorityShiftHistoryDialog
          commitment={openHistory}
          onClose={() => setOpenHistory(null)}
        />
      )}
      {openComplete && (
        <CompleteDialog
          commitment={openComplete}
          onClose={() => setOpenComplete(null)}
          onDone={() => { setOpenComplete(null); loadAll(); }}
        />
      )}
      {openDelete && (
        <DeleteConfirmDialog
          commitment={openDelete}
          onClose={() => setOpenDelete(null)}
          onDone={() => { setOpenDelete(null); loadAll(); }}
        />
      )}
    </AppShell>
  );
}

// ============================================================ My commitments

function MyCommitments({ data, projects, onOpenEdit, onOpenRootCause, onComplete, onDelete, onOpenHistory }) {
  const projMap = useMemo(() => Object.fromEntries(projects.map(p => [p.id, p])), [projects]);
  const { counts, commitments } = data;
  const [filter, setFilter] = useState('open');
  const filtered = useMemo(() => {
    if (filter === 'open') return commitments.filter(c => c.derived_status === 'open' || c.derived_status === 'overdue');
    if (filter === 'overdue') return commitments.filter(c => c.derived_status === 'overdue');
    if (filter === 'done') return commitments.filter(c => c.derived_status === 'done');
    return commitments;
  }, [commitments, filter]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="commitments-counts">
        <StatTile label="Open" value={counts.open} icon={Target} status="info" onClick={() => setFilter('open')} active={filter === 'open'} testId="count-open" />
        <StatTile label="Overdue" value={counts.overdue} icon={AlertTriangle} status={counts.overdue ? 'danger' : 'ok'} onClick={() => setFilter('overdue')} active={filter === 'overdue'} testId="count-overdue" />
        <StatTile label="Done" value={counts.done} icon={CheckCircle2} status="ok" onClick={() => setFilter('done')} active={filter === 'done'} testId="count-done" />
        <StatTile label="All" value={commitments.length} icon={ClipboardCheck} onClick={() => setFilter('all')} active={filter === 'all'} testId="count-all" />
      </div>
      {filtered.length === 0 && (
        <div className="text-center py-10 border rounded-lg text-[hsl(var(--foreground-muted))] text-[13.5px]" style={{ borderColor: 'hsl(var(--border))' }} data-testid="commitments-empty">
          No commitments here — create one above to start tracking your promises.
        </div>
      )}
      <div className="space-y-2">
        {filtered.map(c => {
          const isOverdue = c.derived_status === 'overdue';
          const isDone = c.derived_status === 'done';
          const overload = !!c.overload_flag;
          const rebaselined = !!c.is_rebaselined;
          const selfMoves = c.self_moves_count || 0;
          const hasShifts = (c.priority_shifts || []).length > 0;
          return (
            <div key={c.id} className="card p-3 flex items-start gap-3" data-testid={`commitment-${c.id}`}
                 style={overload ? { borderColor: 'hsl(var(--danger))' } : {}}>
              <div className={`w-2 h-2 rounded-full mt-2 flex-none ${isOverdue ? 'bg-[hsl(var(--danger))]' : isDone ? 'bg-[hsl(var(--success))]' : 'bg-[hsl(var(--primary))]'}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className={`font-medium ${isDone ? 'line-through opacity-60' : ''}`}>{c.title}</div>
                  <span className={`chip ${STATUS_CHIP[c.derived_status] || ''}`}>{c.derived_status}</span>
                  {c.working_day_delay > 0 && (
                    <span className="chip chip-danger" title="Working-day delay (weekends + holidays excluded)" data-testid={`commitment-wd-delay-${c.id}`}>
                      +{c.working_day_delay} wd late
                    </span>
                  )}
                  {overload && (
                    <span className="chip chip-danger" data-testid={`commitment-overload-${c.id}`}>
                      <Flame size={11} className="inline mr-0.5" />overload
                    </span>
                  )}
                  {!overload && selfMoves > 0 && (
                    <span className="chip chip-warning" title="Self-moved" data-testid={`commitment-selfmove-${c.id}`}>
                      <TimerReset size={11} className="inline mr-0.5" />{selfMoves} self-move{selfMoves === 1 ? '' : 's'}
                    </span>
                  )}
                  {rebaselined && (
                    <span className="chip chip-info" title="Re-baselined by lead/owner — KRA penalty forgiven from this point." data-testid={`commitment-rebaseline-${c.id}`}>
                      <Flag size={11} className="inline mr-0.5" />re-baselined
                    </span>
                  )}
                  {isOverdue && !c.root_cause && (
                    <button className="chip chip-warning" onClick={() => onOpenRootCause(c)} data-testid={`commitment-tag-root-${c.id}`}>
                      <Flag size={11} className="inline mr-0.5" />Tag root cause
                    </button>
                  )}
                  {c.root_cause && (
                    <span className="chip" title={c.root_cause_note || ''} data-testid={`commitment-root-${c.id}`}>
                      <Flag size={11} className="inline mr-0.5" />{c.root_cause.replace(/_/g, ' ')}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-[12px] text-[hsl(var(--foreground-muted))] mt-1 flex-wrap">
                  <Calendar size={11} />Target: <span className="tabular-nums">{c.target_date}</span>
                  {typeof c.days_until_target === 'number' && !isDone && (
                    <span className={isOverdue ? 'text-[hsl(var(--danger))]' : ''}>
                      ({c.days_until_target < 0 ? `${Math.abs(c.days_until_target)} day(s) overdue` : c.days_until_target === 0 ? 'today' : `in ${c.days_until_target} day(s)`})
                    </span>
                  )}
                  {c.actual_date && (
                    <><span>·</span><CheckCircle2 size={11} />Actual: <span className="tabular-nums">{c.actual_date}</span></>
                  )}
                  {c.project_id && projMap[c.project_id] && (
                    <><span>·</span><Layers size={11} />{projMap[c.project_id].name}</>
                  )}
                  {c.stage && (
                    <><span>·</span><span>{STAGE_LABEL[c.stage] || c.stage}</span></>
                  )}
                  {hasShifts && (
                    <button className="text-[hsl(var(--primary))] underline underline-offset-2 hover:opacity-80"
                            onClick={() => onOpenHistory(c)}
                            data-testid={`commitment-history-${c.id}`}>
                      <History size={11} className="inline mr-0.5" />shift history ({c.priority_shifts.length})
                    </button>
                  )}
                </div>
                {c.detail && <div className="text-[12.5px] text-[hsl(var(--foreground-muted))] mt-1">{c.detail}</div>}
              </div>
              <div className="flex-none flex items-center gap-1">
                {!isDone && (
                  <Button size="sm" variant="outline" onClick={() => onComplete(c)} data-testid={`commitment-complete-${c.id}`}>
                    <CheckCircle2 size={13} className="mr-1" />Complete
                  </Button>
                )}
                {!isDone && (
                  <button className="p-1" onClick={() => onOpenEdit(c)} data-testid={`commitment-edit-${c.id}`}>
                    <Pencil size={13} />
                  </button>
                )}
                <button className="p-1 text-[hsl(var(--danger))]" onClick={() => onDelete(c)} data-testid={`commitment-delete-${c.id}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatTile({ label, value, icon: Icon, status, onClick, active, testId }) {
  const color = status === 'ok' ? 'hsl(var(--success))' : status === 'danger' ? 'hsl(var(--danger))' : status === 'info' ? 'hsl(var(--primary))' : 'hsl(var(--foreground-muted))';
  return (
    <button onClick={onClick} data-testid={testId}
            className={`card p-4 text-left w-full ${active ? 'ring-2' : 'hover:border-[hsl(var(--border-strong))]'}`}
            style={active ? { ['--tw-ring-color']: color } : {}}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">{label}</div>
          <div className="font-heading text-2xl mt-1 tabular-nums" style={{ color }}>{value}</div>
        </div>
        <div className="w-9 h-9 rounded-md flex items-center justify-center flex-none" style={{ background: 'hsl(var(--surface-2))', color }}><Icon size={16} /></div>
      </div>
    </button>
  );
}

// ============================================================ Wednesday brief

function WednesdayBrief({ brief, isLead }) {
  return (
    <div className="space-y-4" data-testid="brief-panel">
      <div className="card p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">Wednesday Alignment Brief</div>
            <div className="font-heading text-lg mt-0.5">Week of {brief.week_of}</div>
            <div className="text-[12.5px] text-[hsl(var(--foreground-muted))]">{brief.summary}</div>
          </div>
          <div className="text-[11.5px] text-[hsl(var(--foreground-subtle))] flex items-center gap-1">
            <TrendingUp size={11} />Auto-generated {new Date(brief.generated_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <BriefSection title="Commitments at risk" icon={AlertTriangle} count={(brief.commitments_at_risk || []).length} testId="brief-at-risk">
          {(brief.commitments_at_risk || []).length === 0 && <Empty>Nothing at risk. Nice.</Empty>}
          {(brief.commitments_at_risk || []).map(c => (
            <li key={c.id} className="py-1.5" data-testid={`brief-risk-${c.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`chip ${STATUS_CHIP[c.derived_status] || ''}`}>{c.derived_status}</span>
                <span className="text-[13px]">{c.title}</span>
              </div>
              <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] ml-1">
                {c.user_name || c.user_email} · target {c.target_date || c.due_date}
                {c.working_day_delay > 0 && <> · <span className="text-[hsl(var(--danger))]">+{c.working_day_delay} wd late</span></>}
                {c.root_cause && <> · <b>{c.root_cause.replace(/_/g, ' ')}</b></>}
              </div>
            </li>
          ))}
        </BriefSection>

        {isLead && (
          <BriefSection title="Gates pending & who holds them" icon={ShieldCheck} count={(brief.gates_pending || []).length} testId="brief-gates">
            {(brief.gates_pending || []).length === 0 && <Empty>No open gates.</Empty>}
            {(brief.gates_pending || []).map(g => (
              <li key={`${g.project_id}-${g.stage}`} className="py-1.5 text-[12.5px]" data-testid={`brief-gate-${g.project_id}-${g.stage}`}>
                <b>{g.project_name}</b> · {g.stage.replace(/_/g, ' ')}
                <span className="text-[11.5px] text-[hsl(var(--foreground-muted))] ml-1">· {g.status.replace(/_/g, ' ')} · holder: {g.holder}</span>
              </li>
            ))}
          </BriefSection>
        )}

        {isLead && (
          <BriefSection title="Stage movements since last Wednesday" icon={Layers} count={(brief.stage_movements || []).length} testId="brief-stage-moves">
            {(brief.stage_movements || []).length === 0 && <Empty>No stage movements this week.</Empty>}
            {(brief.stage_movements || []).map(p => (
              <li key={p.project_id} className="py-1.5 text-[12.5px]" data-testid={`brief-stage-${p.project_id}`}>
                <b>{p.name}</b> · {STAGE_LABEL[normalizeStage(p.current_stage)]}
                <span className="text-[11.5px] text-[hsl(var(--foreground-muted))] ml-1">· {p.code}</span>
              </li>
            ))}
          </BriefSection>
        )}

        {isLead && (
          <BriefSection title="Open blockers (from timesheet notes)" icon={AlertTriangle} count={(brief.open_blockers || []).length} testId="brief-blockers">
            {(brief.open_blockers || []).length === 0 && <Empty>No blockers reported in timesheet notes.</Empty>}
            {(brief.open_blockers || []).map(b => (
              <li key={b.entry_id} className="py-1.5 text-[12.5px]" data-testid={`brief-blocker-${b.entry_id}`}>
                <b>{b.user_name}</b> · {b.date}
                <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] italic">&ldquo;{b.note}&rdquo;</div>
              </li>
            ))}
          </BriefSection>
        )}

        {isLead && (
          <BriefSection title="Chargeable visits pending invoice" icon={Calendar} count={(brief.chargeable_visits_pending || []).length} testId="brief-visits">
            {(brief.chargeable_visits_pending || []).length === 0 && <Empty>All chargeable visits invoiced.</Empty>}
            {(brief.chargeable_visits_pending || []).map(v => (
              <li key={v.visit_id} className="py-1.5 text-[12.5px]" data-testid={`brief-visit-${v.visit_id}`}>
                <b>{v.project_name}</b> · {v.visit_date}
                <span className="text-[11.5px] text-[hsl(var(--foreground-muted))] ml-1">· {v.purpose}</span>
              </li>
            ))}
          </BriefSection>
        )}

        <BriefSection title="Root-cause distribution (overdue)" icon={BarChart3} count={(brief.root_cause_distribution || []).length} testId="brief-root-dist">
          {(brief.root_cause_distribution || []).length === 0 && <Empty>No overdue commitments — nothing to distribute.</Empty>}
          {(brief.root_cause_distribution || []).map(d => (
            <li key={d.root_cause} className="py-1 text-[12.5px] flex items-center justify-between" data-testid={`brief-dist-${d.root_cause}`}>
              <span>{d.root_cause.replace(/_/g, ' ')}</span>
              <span className="tabular-nums text-[hsl(var(--foreground-muted))]">{d.count} · {d.pct}%</span>
            </li>
          ))}
        </BriefSection>
      </div>

      <div className="card p-4" data-testid="brief-reflection">
        <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">Reflection prompt</div>
        <div className="text-[14px] mt-1">{brief.reflection_prompt || 'Spoken, not typed: one learning from the last week, one improvement for the next.'}</div>
      </div>
    </div>
  );
}

const Empty = ({ children }) => (
  <li className="py-3 text-[12.5px] text-[hsl(var(--foreground-muted))] italic">{children}</li>
);

function BriefSection({ title, icon: Icon, count, children, testId }) {
  return (
    <section className="card p-3" data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Icon size={14} className="text-[hsl(var(--foreground-muted))]" />
          <div className="font-heading font-semibold text-[14px]">{title}</div>
        </div>
        <span className="text-[11.5px] text-[hsl(var(--foreground-subtle))]">{count}</span>
      </div>
      <ul className="divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
        {children}
      </ul>
    </section>
  );
}

// ============================================================ Team view

function TeamCommitments({ data, canShift, pendingShifts, onOpenShift, onOpenHistory, onConfirmShift }) {
  return (
    <div className="space-y-3">
      {canShift && pendingShifts && pendingShifts.length > 0 && (
        <div className="card p-3 border-l-4 space-y-2" style={{ borderLeftColor: 'hsl(var(--warning))', background: 'hsl(var(--warning-bg))' }} data-testid="team-pending-shifts-banner">
          <div className="font-heading font-semibold text-[14px] flex items-center gap-2">
            <Flag size={14} />{pendingShifts.length} priority shift{pendingShifts.length === 1 ? '' : 's'} pending confirmation
          </div>
          <ul className="divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
            {pendingShifts.map(r => (
              <li key={r.commitment_id} className="py-1.5 flex items-center justify-between text-[12.5px]" data-testid={`team-pending-shift-${r.commitment_id}`}>
                <div className="min-w-0">
                  <b>{r.user_name}</b> · {r.title}
                  <span className="text-[hsl(var(--foreground-muted))] ml-2">→ {r.current_target}</span>
                  {r.pending_shifts?.[0]?.reason && (
                    <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] italic mt-0.5">
                      &ldquo;{r.pending_shifts[0].reason}&rdquo; — by {r.pending_shifts[0].moved_by_name}
                    </div>
                  )}
                </div>
                <Button size="sm" variant="outline"
                        data-testid={`team-pending-shift-confirm-${r.commitment_id}`}
                        onClick={() => onConfirmShift(r.commitment_id, r.pending_shifts[0].id)}>
                  <CheckCircle2 size={13} className="mr-1" />Confirm
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {data.rows.map(r => (
        <div key={r.user_id} className="card p-3" data-testid={`team-row-${r.user_id}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-medium">{r.name || r.email}</div>
              <div className="text-[11.5px] capitalize text-[hsl(var(--foreground-muted))]">{r.role?.replace('_', ' ')}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="chip chip-info">{r.open_count} open</span>
              <span className={`chip ${r.overdue_count ? 'chip-danger' : ''}`}>{r.overdue_count} overdue</span>
              <span className="chip chip-success">{r.done_count} done</span>
              {r.overload_count > 0 && (
                <span className="chip chip-danger" title="Commitments the user has moved more than once">
                  <Flame size={11} className="inline mr-0.5" />{r.overload_count} overload
                </span>
              )}
            </div>
          </div>
          {r.commitments.length > 0 && (
            <ul className="mt-2 divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
              {r.commitments.slice(0, 5).map(c => (
                <li key={c.id} className="py-1.5 text-[12.5px] flex items-center justify-between gap-2" data-testid={`team-commit-${c.id}`}>
                  <div className="min-w-0 flex-1">
                    <span className={`chip ${STATUS_CHIP[c.derived_status] || ''}`}>{c.derived_status}</span>
                    <span className="ml-2">{c.title}</span>
                    <span className="text-[hsl(var(--foreground-subtle))] ml-2">· target {c.target_date}</span>
                    {c.working_day_delay > 0 && (
                      <span className="chip chip-danger ml-2" title="Working-day delay">+{c.working_day_delay} wd</span>
                    )}
                    {c.overload_flag && (
                      <span className="chip chip-danger ml-2"><Flame size={10} className="inline mr-0.5" />overload</span>
                    )}
                    {c.is_rebaselined && (
                      <span className="chip chip-info ml-2"><Flag size={10} className="inline mr-0.5" />re-baselined</span>
                    )}
                    {c.root_cause && <span className="text-[hsl(var(--foreground-muted))] ml-2">· {c.root_cause.replace(/_/g, ' ')}</span>}
                  </div>
                  <div className="flex items-center gap-1 flex-none">
                    {(c.priority_shifts || []).length > 0 && (
                      <button className="text-[hsl(var(--primary))] text-[11.5px] underline underline-offset-2"
                              onClick={() => onOpenHistory(c)}
                              data-testid={`team-commit-history-${c.id}`}>
                        history
                      </button>
                    )}
                    {canShift && c.derived_status !== 'done' && c.derived_status !== 'cancelled' && (
                      <Button size="sm" variant="ghost" className="h-6 px-1.5"
                              onClick={() => onOpenShift(c)}
                              data-testid={`team-commit-shift-${c.id}`}>
                        <ArrowRight size={11} className="mr-1" />Shift
                      </Button>
                    )}
                  </div>
                </li>
              ))}
              {r.commitments.length > 5 && <li className="py-1 text-[11.5px] text-[hsl(var(--foreground-subtle))]">+ {r.commitments.length - 5} more</li>}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

function PriorityShiftHistoryDialog({ commitment, onClose }) {
  const shifts = commitment.priority_shifts || [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="shift-history-dialog">
        <DialogHeader>
          <DialogTitle>Priority-shift history</DialogTitle>
          <DialogDescription>
            All target-date changes on <b>&ldquo;{commitment.title}&rdquo;</b>.
          </DialogDescription>
        </DialogHeader>
        {shifts.length === 0 && (
          <div className="text-[13px] text-[hsl(var(--foreground-muted))] italic">No shifts recorded.</div>
        )}
        <ul className="divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
          {shifts.map(s => (
            <li key={s.id} className="py-2 text-[12.5px]" data-testid={`shift-history-${s.id}`}>
              <div className="flex items-center flex-wrap gap-1">
                <span className="chip">{s.status === 'confirmed' ? 'confirmed' : 'pending confirmation'}</span>
                <span className="tabular-nums">{s.from_target_date || '?'}</span>
                <ArrowRight size={11} />
                <span className="tabular-nums">{s.to_target_date}</span>
                <span className="text-[hsl(var(--foreground-muted))] ml-1">by {s.moved_by_name} ({(s.moved_by_role || '').replace(/_/g, ' ')})</span>
              </div>
              <div className="text-[12px] text-[hsl(var(--foreground-muted))] mt-1 italic">&ldquo;{s.reason}&rdquo;</div>
              {s.shifted_for_project && (
                <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-0.5" data-testid={`shift-history-for-${s.id}`}>
                  Shifted for: <b>{s.shifted_for_project}</b>
                </div>
              )}
              {s.status === 'confirmed' && s.confirmed_by_name && (
                <div className="text-[11.5px] text-[hsl(var(--foreground-subtle))] mt-0.5">
                  Confirmed by {s.confirmed_by_name} on {(s.confirmed_at || '').slice(0, 10)}
                </div>
              )}
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShiftDigestPanel({ digest, loading, onOpenHistory, onReload }) {
  if (loading && !digest) {
    return (
      <div className="card p-6 text-[13px] text-[hsl(var(--foreground-muted))] italic" data-testid="digest-loading">
        Assembling shift digest…
      </div>
    );
  }
  if (!digest) return null;
  const s = digest.summary;
  const nothingHappened = (s.shift_events + s.overloads) === 0;
  return (
    <div className="space-y-3" data-testid="shift-digest-panel">
      <div className="card p-4">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <div className="font-heading text-[16px] font-semibold flex items-center gap-2">
              <Send size={16} />Priority-shift digest
              <span className="chip">{digest.window_days} d rolling</span>
            </div>
            <div className="text-[12.5px] text-[hsl(var(--foreground-muted))] mt-1">
              Since <b className="tabular-nums">{digest.since}</b>. Ops gets this as a notification every Monday morning.
              Owner &amp; leads can pull it any time.
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={onReload} data-testid="digest-reload">
            <History size={13} className="mr-1" />Refresh
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
          <SumTile label="Shift events" value={s.shift_events} tone="info" testId="digest-sum-shifts" />
          <SumTile label="Confirmed" value={s.shifts_confirmed} tone="success" testId="digest-sum-confirmed" />
          <SumTile label="Pending" value={s.shifts_pending} tone={s.shifts_pending ? 'warning' : 'muted'} testId="digest-sum-pending" />
          <SumTile label="Overloads" value={s.overloads} tone={s.overloads ? 'danger' : 'muted'} testId="digest-sum-overloads" />
          <SumTile label="People" value={s.people_affected} tone="muted" testId="digest-sum-people" />
        </div>
      </div>

      {nothingHappened && (
        <div className="card p-6 text-center text-[13px] text-[hsl(var(--foreground-muted))]" data-testid="digest-empty">
          A quiet week — nothing shifted or overloaded.
        </div>
      )}

      {digest.overload_events.length > 0 && (
        <div className="card p-4" data-testid="digest-overload-section">
          <div className="font-heading text-[14px] font-semibold flex items-center gap-2 mb-2">
            <Flame size={14} style={{ color: 'hsl(var(--danger))' }} />
            Overload flags <span className="chip chip-danger">{digest.overload_events.length}</span>
          </div>
          <ul className="divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
            {digest.overload_events.map(e => (
              <li key={e.commitment_id + e.flagged_at} className="py-2 text-[12.5px] flex items-center justify-between gap-2"
                  data-testid={`digest-overload-${e.commitment_id}`}>
                <div className="min-w-0">
                  <div className="font-medium">{e.user_name} <span className="text-[hsl(var(--foreground-muted))] font-normal">· {e.commitment_title}</span></div>
                  <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-0.5">
                    Target: <span className="tabular-nums">{e.target_date || '—'}</span> ·
                    self-moves: <b>{e.self_moves_count}</b>
                    {e.working_day_delay > 0 && <> · <span className="text-[hsl(var(--danger))]">+{e.working_day_delay} wd late</span></>}
                    · flagged {(e.flagged_at || '').slice(0, 10)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {digest.shift_events.length > 0 && (
        <div className="card p-4" data-testid="digest-shifts-section">
          <div className="font-heading text-[14px] font-semibold flex items-center gap-2 mb-2">
            <ArrowRight size={14} />Priority shifts <span className="chip chip-info">{digest.shift_events.length}</span>
          </div>
          <ul className="divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
            {digest.shift_events.map((e, idx) => (
              <li key={e.commitment_id + '-' + idx} className="py-2 text-[12.5px]"
                  data-testid={`digest-shift-${e.commitment_id}-${idx}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`chip ${e.status === 'confirmed' ? 'chip-success' : 'chip-warning'}`}>
                    {e.status === 'confirmed' ? 'confirmed' : 'pending'}
                  </span>
                  <span className="font-medium">{e.user_name}</span>
                  <span className="text-[hsl(var(--foreground-muted))]">·</span>
                  <span>{e.commitment_title}</span>
                </div>
                <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-1 flex items-center gap-1 flex-wrap">
                  <span className="tabular-nums">{e.from_target_date || '—'}</span>
                  <ArrowRight size={11} />
                  <span className="tabular-nums">{e.to_target_date}</span>
                  <span>· by <b>{e.moved_by_name}</b> ({(e.moved_by_role || '').replace(/_/g, ' ')})</span>
                  <span>· {(e.created_at || '').slice(0, 10)}</span>
                </div>
                {e.reason && (
                  <div className="text-[12px] text-[hsl(var(--foreground-muted))] italic mt-1 pl-2 border-l-2"
                       style={{ borderColor: 'hsl(var(--border))' }}>
                    &ldquo;{e.reason}&rdquo;
                    {e.shifted_for_project && <span className="not-italic"> — for <b>{e.shifted_for_project}</b></span>}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {digest.root_cause_distribution?.length > 0 && (
        <div className="card p-4" data-testid="digest-root-causes">
          <div className="font-heading text-[14px] font-semibold flex items-center gap-2 mb-2">
            <Flag size={13} />Root causes (this window)
          </div>
          <div className="flex flex-wrap gap-2">
            {digest.root_cause_distribution.map(rc => (
              <span key={rc.root_cause} className="chip" data-testid={`digest-rc-${rc.root_cause}`}>
                {rc.root_cause.replace(/_/g, ' ')} · <b>{rc.count}</b>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SumTile({ label, value, tone = 'muted', testId }) {
  const border = tone === 'muted' ? 'hsl(var(--border))' : `hsl(var(--${tone}))`;
  return (
    <div className="border rounded-md p-3 text-center" style={{ borderColor: border }} data-testid={testId}>
      <div className="text-[22px] font-heading font-semibold tabular-nums">{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-[hsl(var(--foreground-muted))]">{label}</div>
    </div>
  );
}


// ============================================================ Dialogs

function CommitmentDialog({ mode, meta, projects, initial, onClose, onDone }) {
  const isEdit = mode === 'edit';
  const [title, setTitle] = useState(initial?.title || '');
  const [detail, setDetail] = useState(initial?.detail || '');
  const [targetDate, setTargetDate] = useState(initial?.target_date || initial?.due_date || todayISO());
  const [projectId, setProjectId] = useState(initial?.project_id || '');
  const [stage, setStage] = useState(initial?.stage || '');
  const [saving, setSaving] = useState(false);

  const canSave = title.trim().length > 0 && targetDate;

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        title: title.trim(),
        detail: detail.trim() || null,
        target_date: targetDate,
        project_id: projectId || null,
        stage: stage || null,
      };
      if (isEdit) {
        await api.patch(`/commitments/${initial.id}`, body);
        toast.success('Updated.');
      } else {
        await api.post('/commitments', body);
        toast.success('Commitment created.');
      }
      onDone();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="commitments-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit commitment' : 'New commitment'}</DialogTitle>
          <DialogDescription>
            Set a target date you believe. Moving it later is allowed and logged — a moved commitment is information.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Freeze concept typography and share final direction" data-testid="commitments-title" />
          </div>
          <div>
            <Label>Detail (optional)</Label>
            <Textarea rows={2} value={detail} onChange={e => setDetail(e.target.value)} placeholder="A line or two if it helps future you or your lead." data-testid="commitments-detail" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Target date</Label>
              <Input type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)} data-testid="commitments-target" />
            </div>
            <div>
              <Label>Project (optional)</Label>
              <select className="h-9 w-full border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                      value={projectId} onChange={e => setProjectId(e.target.value)} data-testid="commitments-project">
                <option value="">— None —</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Stage (optional)</Label>
              <select className="h-9 w-full border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                      value={stage} onChange={e => setStage(e.target.value)} data-testid="commitments-stage">
                <option value="">— None —</option>
                {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!canSave || saving} data-testid="commitments-save">
            {saving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RootCauseDialog({ commitment, meta, onClose, onDone }) {
  const [rootCause, setRootCause] = useState('unclear_brief');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const canSave = rootCause && (rootCause !== 'other' || note.trim().length > 0);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/commitments/${commitment.id}/root_cause`, {
        root_cause: rootCause,
        note: note.trim() || null,
      });
      toast.success('Root cause noted. Ten seconds — no essays needed.');
      onDone();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="root-cause-dialog">
        <DialogHeader>
          <DialogTitle>Tag a root cause — 10 seconds</DialogTitle>
          <DialogDescription>
            &ldquo;{commitment.title}&rdquo; is overdue. Pick a root cause — this is Level 0: honest, factual, no essay.
            Patterns are handled by the escalation SOP (G4) in its supportive framing.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {(meta?.root_causes || []).map(rc => (
            <label key={rc.id} className={`flex items-start gap-2 border rounded-md p-2 cursor-pointer transition-colors ${rootCause === rc.id ? 'border-[hsl(var(--primary))] bg-[hsl(var(--accent))]' : 'hover:border-[hsl(var(--border-strong))]'}`}
                   style={{ borderColor: rootCause === rc.id ? 'hsl(var(--primary))' : 'hsl(var(--border))' }}
                   data-testid={`root-cause-${rc.id}`}>
              <input type="radio" checked={rootCause === rc.id} onChange={() => setRootCause(rc.id)} className="mt-1" />
              <div className="text-[13.5px]">{rc.label}</div>
            </label>
          ))}
          {rootCause === 'other' && (
            <div>
              <Label>Short note (required for &ldquo;Other&rdquo;)</Label>
              <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="One line is enough." data-testid="root-cause-note" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!canSave || saving} data-testid="root-cause-save">
            {saving ? 'Saving…' : 'Tag it'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Label = ({ children }) => (
  <div className="text-[12px] text-[hsl(var(--foreground-muted))] mb-1">{children}</div>
);

function CompleteDialog({ commitment, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [actualDate, setActualDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/commitments/${commitment.id}/complete`, {
        note: note.trim() || null,
        actual_date: actualDate,
      });
      toast.success('Marked complete.');
      onDone();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="complete-dialog">
        <DialogHeader>
          <DialogTitle>Mark complete</DialogTitle>
          <DialogDescription>
            Completing <b>&ldquo;{commitment.title}&rdquo;</b>. The actual date feeds the working-day delay math.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Actual completion date</Label>
            <Input type="date" value={actualDate} onChange={e => setActualDate(e.target.value)} data-testid="complete-actual-date" />
          </div>
          <div>
            <Label>Completion note (optional)</Label>
            <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)}
                      placeholder="One line if it helps — e.g. handed over to fabricator." data-testid="complete-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="complete-save">
            <CheckCircle2 size={13} className="mr-1" />{saving ? 'Saving…' : 'Complete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteConfirmDialog({ commitment, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const doDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/commitments/${commitment.id}`);
      toast.success('Deleted.');
      onDone();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="delete-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Delete commitment?</DialogTitle>
          <DialogDescription>
            &ldquo;{commitment.title}&rdquo; will be removed permanently, including its shift history. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="delete-cancel-btn">Cancel</Button>
          <Button variant="destructive" onClick={doDelete} disabled={saving} data-testid="delete-confirm-btn">
            <Trash2 size={13} className="mr-1" />{saving ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
